import dayjs from "dayjs";
import Image from "next/image";
import React, { useState } from "react";
import { Heart } from "react-iconly";
import relativeTime from "dayjs/plugin/relativeTime";
import axios from "axios";
import { getAuth } from "firebase/auth";
import Link from "next/link";
import {
  BsThreeDots,
  BsThreeDotsVertical,
  BsPinAngle,
  BsPinAngleFill,
} from "react-icons/bs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "@jammable/ui-components/DropdownMenu";
import { FaTrash } from "react-icons/fa";
import DeleteCommentModal from "../components/modals/DeleteCommentModal";
import { useModal } from "@/context/ModalContext";
import { useAuth } from "@/context/AuthProvider";
import { AiFillWarning } from "react-icons/ai";
import ReportModal from "./modals/ReportModal";
import useAuthModal from "@/hooks/useAuthModal";
import { Input } from "@jammable/ui-components/Forms";
import { Button, SecondaryButton, toast } from "@jammable/ui-core";
import { useFormik } from "formik";
import * as yup from "yup";
import { useQueryClient } from "react-query";
import { Spinner } from "@jammable/ui-components/Loader";
import Dropdown from "./dropdown/Dropdown.js";
import { useRouter } from "next/router";

const ModelCommentItem = ({ comment, model, page, className }) => {
  dayjs.extend(relativeTime);
  const [likes, setLikes] = useState(comment?.likes ?? 0);
  const [isLiked, setIsLiked] = useState(comment?.isLiked ?? false);
  const { checkAuthAndExecute } = useAuthModal();
  const [isReplyOpen, setIsReplyOpen] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [visibleReplies, setVisibleReplies] = useState(2);
  const [isLoadingReplies, setIsLoadingReplies] = useState(false);
  const [replies, setReplies] = useState([]);
  const [replyPagination, setReplyPagination] = useState({
    currentPage: 0,
    totalPages: 1,
    totalReplies: 0,
  });

  const { user } = useAuth();
  const formatRelativeTime = (date) => {
    const duration = dayjs().diff(dayjs(date), "minute");
    if (duration < 1) return "just now";
    if (duration < 60) return `${duration}m`;
    const hours = Math.floor(duration / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const validationSchema = yup.object().shape({
    reply: yup
      .string()
      .required("Text is required")
      .max(500, "Maximum 500 characters allowed!"),
  });

  const fetchReplies = async (page = 1) => {
    setIsLoadingReplies(true);

    try {
      const auth = getAuth();
      const token = await auth?.currentUser?.getIdToken();
      const res = await axios.get("/api/comment/getCommentReplies", {
        params: {
          commentId: comment._id,
          page,
          limit: 5,
        },
        headers: {
          Authorization: token,
        },
      });

      setReplies((prev) =>
        page === 1 ? res.data.replies : [...prev, ...res.data.replies],
      );
      setReplyPagination(res.data.pagination);
    } catch (error) {
      console.error("Error fetching replies:", error);
      toast.error("Failed to load replies");
    } finally {
      setIsLoadingReplies(false);
    }
  };

  const loadMoreReplies = () => {
    if (replyPagination.currentPage < replyPagination.totalPages) {
      fetchReplies(replyPagination.currentPage + 1);
    }
  };

  const formik = useFormik({
    initialValues: {
      reply: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!checkAuthAndExecute()) return false;
      if (values.reply.trim().length === 0) return;
      if (!checkAuthAndExecute()) return false;
      setCommenting(true);
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();
      axios
        .post(
          `/api/comment/createComment`,
          {
            body: values.reply,
            modelId: model?._id,
            parentid: comment._id,
          },
          {
            headers: { Authorization: token },
          },
        )
        .then(() => {
          queryClient.invalidateQueries(["comments", model?._id]).then(() => {
            toast.success("Comment posted!");
            setCommenting(false);
            setIsReplyOpen(false);
            formik.setFieldValue("reply", "");
          });
        })
        .catch((err) => {
          setCommenting(false);
          toast.error(err?.response?.data?.message);
        });
    },
  });
  const router = useRouter();

  return (
    <div
      onClick={() => {}}
      key={comment?._id}
      className={`tw-flex tw-flex-col tw-gap-2 tw-justify-between tw-items-center ${
        comment.parentid && "tw-p-1"
      } ${className}`}
    >
      <div className="tw-flex tw-justify-between tw-w-full">
        <div className="tw-flex tw-gap-3">
          <Image
            alt="User Avatar"
            className="tw-rounded-3xl"
            src={comment?.user?.avatar}
            width={40}
            height={40}
            onClick={() => {
              router.push(`/@${comment?.user?.username}`);
            }}
          />
          <div className="tw-flex tw-flex-col tw-text-sm tw-font-semibold">
            <div className=" tw-flex ">
              <Link
                href={`/@${comment?.user?.username}`}
                className="tw-font-bold"
              >
                {comment?.user?.username}
              </Link>

              {model?.accessIds?.includes(comment.userid) && (
                <div className="tw-flex tw-items-center tw-bg-purple-100 tw-rounded-full tw-ml-2 tw-text-xs tw-font-bold  tw-px-2 tw-text-purple-700">
                  Creator
                </div>
              )}
              <span className="tw-opacity-30 tw-ml-2 tw-text-sm tw-font-bold">
                {formatRelativeTime(comment?.createdAt)}
              </span>
            </div>
            <div className="tw-mt-1"> {comment?.body}</div>

            <div className="tw-flex tw-mt-2 tw-w-full tw-items-center">
              {comment.pinned && (
                <div className="tw-flex tw-items-center tw-gap-1 tw-text-black/40 tw-text-xs  tw-mr-2 ">
                  <BsPinAngleFill size={14} />
                  Pinned
                </div>
              )}
              <div
                onClick={async () => {
                  if (!checkAuthAndExecute()) return false;
                  const auth = getAuth();
                  const token = await auth.currentUser.getIdToken();

                  if (isLiked) {
                    setIsLiked(false);
                    setLikes(likes - 1);
                  } else {
                    setIsLiked(true);
                    setLikes(likes + 1);
                  }

                  axios.post(
                    `/api/comment/likeComment`,
                    { commentId: comment?._id },
                    {
                      headers: {
                        Authorization: token,
                      },
                    },
                  );
                }}
                className="tw-flex tw-cursor-pointer tw tw-text-md tw-items-center tw-gap-1 tw-font-bold"
              >
                <Heart
                  size={16}
                  set={isLiked ? "bold" : "light"}
                  primaryColor={isLiked ? "#782dc8" : "rgba(0,0,0,0.4)"}
                />
                <span className="tw-mt-0.5 tw-text-xs tw-normal-nums">
                  {likes}
                </span>
              </div>
              {comment.depth >= 0 || comment.loading ? null : (
                <div
                  onClick={() => {
                    setIsReplyOpen(!isReplyOpen);
                  }}
                  className="tw-cursor-pointer tw-flex tw-items-center tw-bg-gray-100 tw-rounded-full tw-ml-2 tw-text-xs tw-font-bold  tw-px-2 tw-text-gray-700"
                >
                  Reply
                </div>
              )}
            </div>
          </div>
        </div>
        <div>
          <Dropdown
            dropdownItems={
              <>
                {model?.accessIds?.includes(user?.firebaseUid) && (
                  <DropdownMenuItem
                    onClick={async () => {
                      const auth = getAuth();
                      const token = await auth.currentUser.getIdToken();

                      try {
                        const res = await axios.post(
                          `/api/comment/pinComment`,
                          { commentId: comment?._id },
                          {
                            headers: { Authorization: token },
                          },
                        );

                        queryClient.invalidateQueries(["comments", model?._id]);
                        toast.success(
                          res.data.pinned
                            ? "Comment pinned!"
                            : "Comment unpinned!",
                        );
                      } catch (err) {
                        toast.error("Failed to pin comment");
                      }
                    }}
                    className="tw-p-2 tw-pl-3 tw-flex tw-items-center tw-gap-2  hover:tw-bg-gray-100 tw-font-bold tw-cursor-pointer tw-rounded-3xl"
                  >
                    <BsPinAngleFill size={16} />
                    {comment.pinned ? "Unpin Comment" : "Pin Comment"}
                  </DropdownMenuItem>
                )}
                {comment?.userid === user?.firebaseUid ? (
                  <DropdownMenuItem
                    onClick={() => {
                      openModal(
                        "delete-comment-modal",
                        <DeleteCommentModal
                          comment={comment}
                          // sortBy={sortBy}
                          closeModal={() => closeModal("delete-comment-modal")}
                        />,
                      );
                    }}
                    className="tw-p-2 tw-text-red-500 tw-pl-3 tw-flex tw-items-center  tw-gap-2 tw-bg-red-100 hover:tw-bg-red-200 tw-font-bold tw-cursor-pointer tw-rounded-3xl"
                  >
                    <FaTrash size={16} />
                    Delete Comment
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    onClick={() => {
                      if (!checkAuthAndExecute()) return false;
                      openModal(
                        "report-modal",
                        <ReportModal
                          comment={comment}
                          closeModal={() => closeModal("delete-comment-modal")}
                          item={comment}
                          type="comment"
                        />,
                      );
                    }}
                    className="tw-p-2 tw-text-red-500 tw-pl-3 tw-flex tw-items-center  tw-gap-2 tw-bg-red-100 hover:tw-bg-red-200 tw-font-bold tw-cursor-pointer tw-rounded-3xl"
                  >
                    <AiFillWarning size={18} />
                    Report Comment
                  </DropdownMenuItem>
                )}
              </>
            }
          >
            <div className="tw-p-2 tw-width-5 tw-transition-all tw-height-5 tw-flex tw-items-center tw-justify-center hover:tw-bg-gray-100  tw-rounded-lg tw-cursor-pointer">
              <BsThreeDots size={15} />
            </div>
          </Dropdown>
        </div>
      </div>
      {isReplyOpen && (
        <form
          onSubmit={formik.handleSubmit}
          className="tw-w-full tw-flex tw-gap-2 tw-items-end"
        >
          <Input
            aria-label="Comment Reply"
            placeholder="What do you think?!"
            rightSectionWidth="80"
            className="tw-w-full tw-mt-0 tw-h-10 tw-rounded-full"
            value={formik.values.reply}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.reply && formik.errors.reply}
            name="reply"
            disabled={commenting}
          />
          <Button
            className="tw-h-10 tw-w-20 tw-text-xs tw-rounded-full"
            type="submit"
          >
            {commenting ? (
              <Spinner className="tw-border-white" size={4} />
            ) : (
              "Reply"
            )}
          </Button>
        </form>
      )}
      {replies.map((reply) => (
        <ModelCommentItem
          key={reply._id}
          comment={reply}
          page={page}
          model={model}
          className="tw-w-full tw-pl-14"
        />
      ))}

      {replyPagination.totalPages > replyPagination.currentPage &&
        !comment.parentid &&
        comment.replyCount > 0 && (
          <div className="tw-w-full tw-flex tw-justify-center tw-gap-2 tw-items-center">
            {/* <div className="tw-w-full tw-h-0.5 tw-bg-gray-200 tw-rounded-full" /> */}

            <SecondaryButton
              style={{
                fontSize: 14,
                padding: 14,
                paddingTop: 8,
                paddingBottom: 8,
                height: 40,
                display: "flex",
              }}
              onClick={() => {
                if (replies.length === 0) {
                  fetchReplies(1);
                } else {
                  loadMoreReplies();
                }
              }}
              className="tw-mt-1 tw-mb-2 tw-w-max tw-border-none tw-text-black tw-rounded-full tw-font-bold"
              variant="secondary"
            >
              View {comment.replyCount - replyPagination.currentPage * 5}{" "}
              {replyPagination.currentPage === 0
                ? comment.replyCount === 1
                  ? "Reply"
                  : "Replies"
                : "More"}
            </SecondaryButton>
            {/* <div className="tw-w-full tw-h-0.5 tw-bg-gray-200 tw-rounded-full" /> */}
          </div>
        )}
    </div>
  );
};

export default ModelCommentItem;
